import { hasReminder } from './reminders.js';
/**
 * L'hero della home: il riquadro a tutta larghezza in cima (03-SCHERMATE-TV §3.1).
 *
 * ## La regola che decide tutto: una slide che non porta da nessuna parte non si disegna
 *
 * La vetrina pubblica `kind` e `ref`, non il testo. Il testo viene da **ciò a cui `ref` punta**: un
 * film del catalogo, un evento della tipologia. Se quel riferimento non si risolve — il film non è nel
 * pacchetto di questa linea, l'evento è finito, la tipologia non è stata chiesta — la slide **sparisce**
 * invece di disegnarsi vuota.
 *
 * Non è prudenza: è il posto più visibile dello schermo. Un riquadro da 680 px con un titolo mancante
 * e due pulsanti che non fanno niente è peggio di nessun hero, perché chi guarda ci prova e non
 * succede nulla — e chi amministra non ha modo di accorgersene.
 *
 * ## Perché il testo non si configura
 *
 * Perché altrimenti direbbe una cosa e l'evento un'altra. Titolo, competizione, canale e orario
 * arrivano dal dato vero: se la partita slitta di mezz'ora, l'hero slitta con lei. L'unica cosa che
 * l'operatore scrive è **l'immagine**, che il dato non porta.
 */

import { watchStateOf, type VodItem, type WatchProgress, type WatchState } from './catalog.js';
import { eventWhen, type EventWhen } from './eventWhen.js';
import { playableChannelOf, type SportEventView } from './sportEvents.js';

/** Cosa succede premendo il pulsante principale. È sempre qualcosa: vedi la regola qui sopra. */
export type HeroAction =
  /** Apri la scheda di un film o di una serie. */
  | { readonly kind: 'openVod'; readonly itemId: string }
  /** Vai sul canale: l'evento è in onda adesso. */
  | { readonly kind: 'watchChannel'; readonly channelId: string }
  /** Accendi o spegni il promemoria: l'evento non è ancora cominciato. */
  | { readonly kind: 'remind'; readonly channelId: string; readonly startMs: number };

export interface HeroSlide {
  /** Stabile fra un disegno e l'altro: è la chiave di React e il pallino selezionato. */
  readonly id: string;
  /** `EVENTO · STASERA 20:45`, `SERIE A`, `FILM`. Sopra il titolo, in ambra. */
  readonly kicker: string;
  readonly title: string;
  /**
   * La riga sotto il titolo di un **film o una serie**: `Azione · 2024`.
   *
   * Vuota su un evento, che ha due campi suoi — `when` e `channel` — perché la sua riga dipende
   * dalla lingua: «Stasera alle 20:45 · Sul canale Arena Calcio» non si compone qui.
   *
   * Il commento di questo campo prometteva `Su Arena Calcio · iniziata alle 20:45` e il codice
   * scriveva il solo nome del canale: l'orario era dichiarato qui e non è mai esistito.
   */
  readonly meta: string;
  /**
   * Quando è l'evento, come caso e non come frase.
   *
   * `null` su un film: un film non ha un orario, ha un pulsante. La frase la compone chi disegna,
   * perché il nome del giorno e la forma dell'ora li sa `Intl` — vedi `eventWhen.ts`.
   */
  readonly when: EventWhen | null;
  /** Il canale su cui si guarda l'evento, già scelto fra quelli di questa linea. `null` su un film. */
  readonly channel: string | null;
  readonly image: string | null;
  /** Vero per un evento in corso: il distintivo rosso. */
  readonly live: boolean;
  /** Il pulsante principale, e cosa fa. La parola la compone chi disegna, dal codice. */
  readonly primary: { readonly labelKey: string; readonly action: HeroAction };
  /** Il secondo pulsante, quando ce n'è uno. */
  readonly secondary: { readonly labelKey: string; readonly action: HeroAction } | null;
  /** L'avanzamento, per un film già iniziato: decide se il pulsante dice «Riprendi». */
  readonly state: WatchState | null;
}

/** Nessun promemoria. Una costante e non `new Set()` in linea: non si ricrea a ogni chiamata. */
const EMPTY_REMINDERS: ReadonlySet<string> = new Set();

export interface HeroInput {


  readonly heroes: ReadonlyArray<{ readonly kind: string; readonly ref: string; readonly image: string | null }>;
  readonly vod: ReadonlyArray<VodItem>;
  readonly progress: WatchProgress;
  /**
   * I promemoria già impostati, per riflettere lo stato sul tasto.
   *
   * Senza, la slide dice **sempre** «Imposta promemoria», anche su un evento che ne ha già uno: il
   * tasto funziona — lo toglie — ma lo schermo non dice mai quel che è successo, e premendolo due
   * volte si cancella il promemoria credendo di metterlo.
   *
   * Facoltativo per non obbligare il banco di misura a costruirlo: assente vale «nessuno».
   */
  readonly reminders?: ReadonlySet<string>;
  /** Gli eventi già chiesti, per tipologia: le stesse mappe della home. */
  readonly eventsByKind: ReadonlyMap<string, ReadonlyArray<SportEventView>>;
  /**
   * I canali **di questa linea**, per due cose: scegliere quale aprire e scrivere «Su Arena Calcio»
   * invece di un UUID.
   *
   * Era una mappa di soli nomi, e non bastava. Un evento porta gli identificativi di **tutti** i
   * canali su cui va in onda — la chiave in Redis è una sola per l'installazione, non per linea — e
   * l'hero apriva il primo dell'elenco senza chiedersi se questa linea ce l'avesse. Su una linea che
   * non lo vende: riquadro disegnato, distintivo «in diretta», si preme OK e non parte niente.
   *
   * Vedi `playableChannelOf`, che sceglie fra quelli posseduti — e per farlo ha bisogno del numero,
   * che una mappa di nomi non porta.
   */
  readonly channels: ReadonlyArray<{ readonly id: string; readonly number: number; readonly name: string }>;
  /**
   * L'istante del server, lo stesso con cui `liveness.ts` decide chi è in diretta.
   *
   * Serve a sapere se un evento è oggi, domani o più in là, e **non si prende qui con `Date.now()`**:
   * sarebbe l'orologio dell'apparecchio, che su un televisore può essere sbagliato di ore — e si
   * avrebbe un evento annunciato «stasera» da un hero e «domani» dalla riga sotto, con due orologi
   * diversi nella stessa schermata.
   */
  readonly nowMs: number;
}

/** Quante slide si mostrano. Cinque, come il tetto che il backend impone. */
export const HERO_LIMIT = 5;

/**
 * Ogni quanto ruota l'hero.
 *
 * Sette secondi è il prototipo, e il numero ha una ragione: con dieci slide da sette secondi la prima
 * torna dopo più di un minuto, cioè non la vede nessuno. Per questo il tetto è cinque.
 */
export const HERO_ROTATION_MS = 7_000;

function findEvent(
  eventsByKind: ReadonlyMap<string, ReadonlyArray<SportEventView>>,
  ref: string,
): SportEventView | null {
  for (const views of eventsByKind.values()) {
    for (const view of views) if (view.event.id === ref) return view;
  }
  return null;
}

/**
 * Le slide da disegnare, nell'ordine della vetrina.
 *
 * Quelle che non si risolvono si saltano **in silenzio verso chi guarda** — non c'è niente da dirgli —
 * ma `unresolved` le conta, perché chi amministra deve poterlo sapere.
 */
export function heroSlides(input: HeroInput): {
  readonly slides: ReadonlyArray<HeroSlide>;
  /** Quanti riferimenti non si sono risolti. Serve alla diagnosi, non al disegno. */
  readonly unresolved: number;
} {
  const byId = new Map(input.vod.map((item) => [item.id, item]));
  const slides: HeroSlide[] = [];
  let unresolved = 0;

  for (const hero of input.heroes) {
    if (slides.length >= HERO_LIMIT) break;

    if (hero.kind === 'content') {
      const item = byId.get(hero.ref);
      // Il titolo non è nel pacchetto di questa linea: la slide non si disegna.
      if (item === undefined) {
        unresolved += 1;
        continue;
      }
      const state = watchStateOf(item, input.progress);
      const parts = [item.genre, item.year > 0 ? String(item.year) : ''].filter((part) => part !== '');
      slides.push({
        id: `content:${item.id}`,
        kicker: item.kind === 'series' ? 'hero.kicker.series' : 'hero.kicker.movie',
        when: null,
        channel: null,
        title: item.title,
        meta: parts.join(' · '),
        image: hero.image,
        live: false,
        // «Riprendi» o «Guarda»: la differenza la decide l'avanzamento, non chi configura.
        primary: {
          labelKey: state.percent > 0 ? 'hero.action.resume' : 'hero.action.play',
          action: { kind: 'openVod', itemId: item.id },
        },
        secondary: { labelKey: 'hero.action.details', action: { kind: 'openVod', itemId: item.id } },
        state,
      });
      continue;
    }

    if (hero.kind === 'live_event' || hero.kind === 'scheduled_event') {
      const view = findEvent(input.eventsByKind, hero.ref);
      /*
        L'evento non c'è fra quelli caricati: o è finito — `upcomingEvents` toglie i passati — o la
        sua tipologia non è fra quelle che la vetrina nomina, quindi non è stata chiesta. In
        entrambi i casi non c'è niente da mostrare, e l'hero sparisce da solo. È il comportamento
        che il pannello dichiara: «un hero legato a un evento finito sparisce dalla home da solo».
      */
      if (view === null) {
        unresolved += 1;
        continue;
      }
      /*
        Il canale **che questa linea possiede**, non il primo dell'elenco.

        Un evento che questa linea non può guardare non è un hero: sarebbe un pulsante che non apre
        niente, nel posto più visibile dello schermo. E prima `channelIds[0]` poteva benissimo essere
        il canale di un altro pacchetto — l'elenco è comune a tutta l'installazione.
      */
      const channel = playableChannelOf(view.event, input.channels);
      if (channel === null) {
        unresolved += 1;
        continue;
      }
      const channelId = channel.id;
      const live = view.state === 'live';
      const channelName = channel.name;
      slides.push({
        id: `event:${view.event.id}`,
        kicker: view.event.competition,
        title: view.event.title,
        // Vuota: la riga di un evento la compone chi disegna, da `when` e `channel`.
        meta: '',
        when: eventWhen(view.event.startMs, input.nowMs, live),
        channel: channelName,
        image: hero.image,
        live,
        primary: live
          ? { labelKey: 'hero.action.watchLive', action: { kind: 'watchChannel', channelId } }
          : {
              /*
                L'etichetta **riflette lo stato**: «Promemoria impostato» quando ce n'è già uno.

                L'azione resta la stessa — premendo si toglie — ma dicendo sempre «Imposta» il tasto
                mentirebbe sul risultato del tocco precedente. L'identità è `reminderKey`, la stessa
                dell'archivio e della guida: confrontare altro non troverebbe mai niente.
              */
              labelKey: hasReminder(input.reminders ?? EMPTY_REMINDERS, channelId, view.event.startMs)
                ? 'hero.action.reminded'
                : 'hero.action.remind',
              action: { kind: 'remind', channelId, startMs: view.event.startMs },
            },
        // «Vai al canale» anche su un evento futuro: si può volerlo guardare adesso, qualunque cosa
        // ci sia in onda. È il secondo pulsante del prototipo.
        secondary: { labelKey: 'hero.action.goToChannel', action: { kind: 'watchChannel', channelId } },
        state: null,
      });
      continue;
    }

    /*
      `schedule` — una programmazione di canale — **non si risolve da qui**.

      È la forma che costa meno all'operatore, e all'app non arriva: il player non ha una chiamata per
      `channel_schedules`, e inventarsi il titolo da un identificativo non si può. Si conta fra i non
      risolti invece di disegnare un riquadro muto, e quando il gateway porterà la programmazione
      questo ramo diventa uno come gli altri.
    */
    unresolved += 1;
  }

  return { slides, unresolved };
}

/**
 * La slide successiva nella rotazione.
 *
 * Funzione a sé perché la rotazione ha una regola che si dimentica: **si ferma quando il fuoco è
 * dentro l'hero**. Un riquadro che cambia mentre ci si sta muovendo sopra sposta il pulsante sotto il
 * dito, ed è la differenza fra una rotazione elegante e una tortura.
 */
export function nextSlide(current: number, total: number): number {
  if (total <= 0) return 0;
  return (current + 1) % total;
}
